import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/apiClient";
import { GeoData } from "@/types/GeoData";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import GeoMap from "@/components/GeoMap/GeoMap";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DatePickerWithTime } from "@/components/DatePickerWithTime";

const getMoscowTime = (offsetHours: number = 0) => {
    const nowUtc = new Date();
    const nowMoscow = new Date(nowUtc.toLocaleString("en-US", { timeZone: "Europe/Moscow" }));
    return new Date(nowMoscow.getTime() + offsetHours * 60 * 60 * 1000);
};

const AdminMapHistory: React.FC = () => {
    const [types, setTypes] = useState<string[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [newPoint, setNewPoint] = useState<GeoData | null>(null);
    const [speed, setSpeed] = useState<number>(50);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);


    const [fromDate, setFromDate] = useState<Date>(getMoscowTime(-24));
    const [toDate, setToDate] = useState<Date>(getMoscowTime());

    useEffect(() => {
        apiClient.getDeviceTypes().then((data) => {
            const allTypes = data.map((type) => type.id);
            setTypes(allTypes);
            setSelectedTypes(allTypes);
        });
    }, []);

    const startHistoryPlayback = () => {
        if (!fromDate || !toDate || selectedTypes.length === 0) return;

        const fromTimestamp = Math.floor(fromDate.getTime() / 1000);
        const toTimestamp = Math.floor(toDate.getTime() / 1000);

        setIsDisabled(true);

        (async () => {
            await apiClient.connectHistory(
                fromTimestamp,
                toTimestamp,
                speed,
                selectedTypes,
                [],
                (point: GeoData) => setNewPoint(point),
                (error: string) => { alert(`Ошибка: ${error}`); setIsDisabled(false) },
                () => setIsDisabled(false)
            );
        })();
    };

    const toggleType = (type: string) => {
        if (selectedTypes.length === 1 && selectedTypes.includes(type)) return;
        setSelectedTypes((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    return (
        <div className="p-6 space-y-4">
            <h1 className="text-xl font-bold">Админ Карта: История</h1>

            <Card>
                <CardHeader className="font-semibold">Настройки</CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-4">
                        {types.map((type) => (
                            <div key={type} className="flex items-center space-x-2">
                                <Switch
                                    checked={selectedTypes.includes(type)}
                                    disabled={isDisabled}
                                    onCheckedChange={() => toggleType(type)}
                                />
                                <span className="text-sm font-medium">{type}</span>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                        <DatePickerWithTime
                            label="Начало"
                            value={fromDate}
                            onChange={(date) => date && setFromDate(date)}
                            disabled={isDisabled}
                            maxDate={getMoscowTime()}
                        />
                        <DatePickerWithTime
                            label="Конец"
                            value={toDate}
                            onChange={(date) => date && setToDate(date)}
                            disabled={isDisabled}
                            minDate={fromDate}
                            maxDate={getMoscowTime()}
                        />
                        <Select value={speed.toString()} onValueChange={(val) => setSpeed(Number(val))} disabled={isDisabled}>
                            <SelectTrigger className="w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="50">x50</SelectItem>
                                <SelectItem value="100">x100</SelectItem>
                                <SelectItem value="200">x200</SelectItem>
                                <SelectItem value="500">x500</SelectItem>
                                <SelectItem value="1000">x1000</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button onClick={startHistoryPlayback} disabled={isDisabled} className="w-32">
                            {isDisabled ? <Loader2 className="animate-spin w-5 h-5 mr-2" /> : "Запустить"}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card className="max-h-[400px] overflow-hidden">
                <TooltipProvider>
                    <GeoMap deviceTypes={selectedTypes} newPoint={newPoint} />
                </TooltipProvider>
            </Card>
        </div>
    );
};

export default AdminMapHistory;
